import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import {
  Shield,
  Zap,
  Clock,
  ChevronRight,
  Sparkles,
  Star,
  PlayCircle,
  Bot,
  ShoppingBag,
  CreditCard,
  LayoutDashboard,
  Users,
} from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import AuthModal from "@/components/AuthModal";
import SiteFooter from "@/components/SiteFooter";
import ReviewCarousel from "@/components/ReviewCarousel";
import { SITE_VIDEO, VideoPlayer } from "@/components/ProductExtras";

const FEATURES = [
  { icon: Zap, title: "Activation instantanée", description: "Ton produit est disponible juste après l'achat, sans attendre." },
  { icon: Shield, title: "Paiement sécurisé", description: "Paiements protégés et données traitées dans le respect du RGPD." },
  { icon: Clock, title: "Support 24/7", description: "Une question ? L'assistance répond à tout moment." },
  { icon: Sparkles, title: "Mises à jour auto", description: "Les nouveautés arrivent toutes seules, sans rien réinstaller." },
];

const STEPS = [
  { icon: ShoppingBag, title: "Choisis ton produit", text: "Bot de gestion, radio, téléphone FiveM ou anti-cheat : compare et choisis." },
  { icon: CreditCard, title: "Paye en toute sécurité", text: "Paiement en ligne sécurisé, produit activé tout de suite." },
  { icon: LayoutDashboard, title: "Règle tout depuis le site", text: "Tableau de bord, console Discord, journal des logs : tout se gère en ligne." },
];

// Images de démonstration livrées avec le site (public/images).
// Écrans seuls (sans cadre ni légende), découpés dans gestion/demo-1 et vantaphone/demo-yphone-1.
const HERO_SHOTS = { main: "/images/gestion/hero-dashboard.jpg", side: "/images/vantaphone/hero-yphone-ecran.jpg" };

function minPrice(bot) {
  const prices = (bot.plans || []).map((p) => Number(p.price)).filter((p) => p > 0);
  return prices.length ? Math.min(...prices) : null;
}

export default function LandingPage() {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ count: 0, average: 0, active_clients: 0, total_users: 0 });
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews/stats`).then((r) => setStats(r.data || {})).catch(() => {});
    axios.get(`${API}/shop/bots`).then((r) => setProducts(Array.isArray(r.data) ? r.data.slice(0, 6) : [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden">
      <Navbar onAuthClick={() => setShowAuth(true)} />

      {/* En-tête */}
      <section className="relative pt-28 sm:pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-blue-600/20 blur-[120px]" />
          <div className="absolute top-40 -right-40 w-[500px] h-[500px] rounded-full bg-indigo-600/15 blur-[100px]" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-7 text-sm">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-white/70">Nouveau : console Discord et journal des logs en ligne</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
              Bots Discord{" "}
              <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-violet-400 bg-clip-text text-transparent">premium</span>
              {" "}et scripts FiveM pour ta communauté
            </h1>
            <p className="text-lg text-white/60 mb-9 max-w-xl leading-relaxed">
              VantaPulse crée des bots Discord en français (gestion, modération, tickets, giveaways, musique et radio)
              et des scripts FiveM (téléphone, tablette, anti-cheat) prêts à l'emploi pour faire vivre ton serveur.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="get-started-btn"
                onClick={() => (user ? navigate("/shop") : setShowAuth(true))}
                className="bg-blue-600 hover:bg-blue-500 text-white px-7 py-6 text-base font-semibold rounded-xl shadow-lg shadow-blue-600/30 transition-all hover:-translate-y-0.5"
              >
                {user ? "Découvrir nos produits" : "Commencer"}
                <ChevronRight className="ml-1.5 w-5 h-5" />
              </Button>
              {user ? (
                <Button data-testid="dashboard-btn" variant="outline" onClick={() => navigate("/dashboard")}
                  className="border-white/15 bg-white/5 hover:bg-white/10 px-7 py-6 text-base rounded-xl">
                  Mon tableau de bord
                </Button>
              ) : (
                <Button variant="outline" onClick={() => navigate("/shop")} className="border-white/15 bg-white/5 hover:bg-white/10 px-7 py-6 text-base rounded-xl">
                  Voir la boutique
                </Button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4 mt-10" data-testid="hero-stats">
              {stats.count > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-1">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${stats.average >= n - 0.25 ? "text-yellow-400 fill-yellow-400" : "text-white/15"}`} />)}</div>
                  <span className="text-sm text-white/60"><b className="text-white">{stats.average}</b>/5 · {stats.count} avis</span>
                </div>
              )}
              {stats.active_clients > 0 && <div className="flex items-center gap-2 text-sm text-white/60"><Users className="w-4 h-4 text-emerald-400" /><b className="text-white">{stats.active_clients}</b> clients actifs</div>}
              <div className="flex items-center gap-2 text-sm text-white/60"><Shield className="w-4 h-4 text-blue-400" />Paiement sécurisé</div>
            </div>
          </div>

          {/* Aperçus des produits */}
          <div className="relative hidden sm:block" aria-hidden="true">
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-blue-500/20 to-violet-500/10 blur-2xl" />
            <div className="relative rounded-2xl border border-white/10 bg-[#101019] shadow-2xl shadow-black/60 overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/5 bg-white/[0.02]">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" /><span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                <span className="ml-3 text-[11px] text-white/30 font-mono">vanta-pulse.shop/bot-dashboard</span>
              </div>
              <img src={HERO_SHOTS.main} alt="" className="w-full aspect-[1114/556] object-cover" loading="eager" />
            </div>
            <div className="absolute -bottom-10 -left-8 w-36 lg:w-40 rotate-[-6deg] rounded-[1.6rem] border-4 border-[#1b1b24] bg-black shadow-2xl shadow-black/70 overflow-hidden">
              <img src={HERO_SHOTS.side} alt="" className="w-full aspect-[468/1050] object-cover" loading="eager" />
            </div>
            <div className="absolute -top-5 -right-3 flex items-center gap-2 rounded-xl border border-white/10 bg-[#12121a]/90 backdrop-blur px-3 py-2 shadow-xl">
              <span className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center"><Bot className="w-4 h-4 text-emerald-300" /></span>
              <span className="text-xs"><b className="block text-white">Bot en ligne</b><span className="text-white/40">synchronisé avec le site</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* Produits */}
      {products.length > 0 && (
        <section className="py-20 border-t border-white/5" data-testid="home-products">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <div>
                <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest">Nos produits</span>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Tout ce qu'il faut pour ton serveur</h2>
              </div>
              <button type="button" onClick={() => navigate("/shop")} className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white">Toute la boutique<ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((p) => {
                const price = minPrice(p);
                const color = p.color || "#3b82f6";
                return (
                  <button key={p.id} type="button" onClick={() => navigate(`/bot/${p.id}`)} data-testid={`home-product-${p.id}`}
                    className="group text-left rounded-2xl border border-white/5 bg-[#12121a] overflow-hidden transition-all hover:-translate-y-1 hover:border-white/15 hover:shadow-2xl hover:shadow-black/40">
                    <div className="relative h-36 overflow-hidden">
                      {p.banner_url || p.image_url
                        ? <img src={p.banner_url || p.image_url} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                        : <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${color}55, transparent)` }} />}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#12121a] via-[#12121a]/20 to-transparent" />
                    </div>
                    <div className="relative px-5 pb-5 -mt-8">
                      <div className="w-14 h-14 rounded-xl overflow-hidden ring-4 ring-[#12121a] flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
                        {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <Bot className="w-7 h-7" style={{ color }} />}
                      </div>
                      <h3 className="mt-3 text-lg font-semibold">{p.name}</h3>
                      <p className="mt-1 text-sm text-white/50 line-clamp-2 min-h-[2.5rem]">{p.description || (p.downloadable ? "Script prêt à installer sur ton serveur." : "Bot Discord premium, prêt à l'emploi.")}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-sm text-white/60">{price ? <>dès <b className="text-white">{price.toLocaleString("fr-FR")} €</b></> : p.downloadable ? "Script FiveM" : "Bot Discord"}</span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium transition-all group-hover:gap-2" style={{ color }}>Voir<ChevronRight className="w-4 h-4" /></span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Points forts */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest">Pourquoi VantaPulse</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Simple, rapide et toujours à jour</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="relative overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-b from-white/[0.04] to-transparent p-6 transition-colors hover:border-blue-500/30">
                <span className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4"><f.icon className="w-5 h-5 text-blue-400" /></span>
                <h3 className="font-heading text-lg font-semibold mb-1.5">{f.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest">Comment ça marche</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Prêt en trois étapes</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 relative">
            <div className="hidden md:block absolute top-9 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative text-center px-4">
                <div className="relative mx-auto w-[72px] h-[72px] rounded-2xl bg-[#12121a] border border-white/10 flex items-center justify-center shadow-xl shadow-blue-900/20">
                  <s.icon className="w-7 h-7 text-blue-400" />
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-blue-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold mt-5 mb-1.5">{s.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vidéo de présentation du site */}
      <section className="py-20 border-t border-white/5" data-testid="site-video">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 text-blue-400 text-sm font-semibold uppercase tracking-widest"><PlayCircle className="w-4 h-4" />En vidéo</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-3">Découvre VantaPulse en 2 minutes</h2>
            <p className="text-white/55 mt-3">VantaPhone, bots Discord, anti-cheat, tableau de bord : tout ce que le site te propose.</p>
          </div>
          <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl shadow-blue-900/20" style={{ aspectRatio: "16 / 9" }}>
            <VideoPlayer url={SITE_VIDEO.url} preload="none" className="absolute inset-0 w-full h-full" testId="site-video-player" />
          </div>
        </div>
      </section>

      {/* Avis clients (vrais avis publiés) en carrousel */}
      <section className="py-20 border-t border-white/5 overflow-hidden" data-testid="home-reviews">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <ReviewCarousel emptyLink="/shop" />
        </div>
      </section>

      {/* Appel final */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-blue-500/30 bg-gradient-to-br from-blue-600/25 via-indigo-600/10 to-[#101019] p-10 sm:p-14 text-center">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-blue-500/25 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">Prêt à commencer ?</h2>
              <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
                Rejoins les serveurs qui utilisent VantaPulse et fais passer ta communauté au niveau supérieur dès maintenant.
              </p>
              <Button
                data-testid="cta-shop"
                onClick={() => navigate("/shop")}
                className="bg-white text-[#0a0a0f] hover:bg-white/90 px-9 py-6 text-base font-semibold rounded-xl shadow-xl transition-all hover:-translate-y-0.5"
              >
                Voir la boutique
                <ChevronRight className="ml-1.5 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode={authMode} setMode={setAuthMode} />
    </div>
  );
}
